import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage, langPrefix } from "@/i18n/LanguageContext";
import { useUserAuth } from "@/user/UserAuthContext";
import { AUTH_STRINGS } from "@/user/authStrings";
import { AuthCard } from "@/pages/auth/AuthLogin";

/**
 * Handles `/<lang>/auth/callback#session_id=...` after Emergent Google OAuth.
 *
 * Workflow:
 *   1. Pull session_id synchronously from the fragment.
 *   2. POST /api/auth/google/exchange -> either signed-in or onboarding-required.
 *   3. Strip fragment from URL before any navigation.
 *   4. Route to /<lang>/account (signed in) or /<lang>/auth/complete-google
 *      (new user).
 */
export default function AuthCallback() {
  const { lang } = useLanguage();
  const L = AUTH_STRINGS[lang];
  const { googleExchange } = useUserAuth();
  const navigate = useNavigate();
  const ran = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const hash = typeof window !== "undefined" ? window.location.hash || "" : "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    if (!sessionId) {
      setError(L.callbackMissing);
      return;
    }

    (async () => {
      const res = await googleExchange(sessionId, lang);

      // Clear the session_id from the URL immediately.
      if (typeof window !== "undefined" && window.history?.replaceState) {
        const clean = window.location.pathname + window.location.search;
        window.history.replaceState(null, "", clean);
      }

      if (!res.ok) {
        setError(res.error || L.callbackMissing);
        return;
      }
      const prefix = langPrefix(lang);
      if (res.status === "signed_in") {
        navigate(`${prefix}/account` || "/account", { replace: true });
        return;
      }
      // onboarding_required
      navigate(`${prefix}/auth/complete-google` || "/auth/complete-google", { replace: true });
    })();
  }, [L, googleExchange, lang, navigate]);

  if (error) {
    return (
      <AuthCard eyebrow="SAS" title={AUTH_STRINGS[lang].loginTitle}>
        <div className="mt-6 text-[14px] text-red-600" data-testid="callback-error">
          {error}
        </div>
        <button
          type="button"
          onClick={() => navigate(`${langPrefix(lang)}/auth/login` || "/auth/login", { replace: true })}
          className="sas-btn sas-btn-ghost mt-5"
          data-testid="callback-retry"
        >
          {AUTH_STRINGS[lang].retry}
        </button>
      </AuthCard>
    );
  }

  return (
    <div
      className="min-h-[calc(100vh-4rem)] flex items-center justify-center"
      data-testid="callback-working"
    >
      <div className="text-sm text-[color:var(--sas-ink-2)]">{L.callbackWorking}</div>
    </div>
  );
}
